import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getLocale } from "react-i18nify";
import { useSnackbar } from "notistack";

import { FormControlLabel, Grid, Stack } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Divider from "@mui/material/Divider";

import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/EditOutlined";
import CircleIcon from "@mui/icons-material/Circle";
// import HistoryIcon from "@mui/icons-material/History";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SaveAltIcon from "@mui/icons-material/SaveAlt";

import { getPurchaseOrderDetail, updateOrderNote } from "src/api/purchaseOrder";

import font from "src/styles/font";
import { Green, Primary } from "src/styles/color";
import { checkNotEmpty, convertIntToCurrency } from "src/common";
import useDeviceSize from "src/hooks/useDeviceSize";
import { useDialog } from "src/context/DialogContext";
import { useAuth } from "src/hooks/useAuth";

import Main from "src/components/ui/Container";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import Checkbox from "src/components/core/Checkbox";
import Loading from "src/components/ui/Loading";
import {
  Container,
  DivBackground,
  DivPaymentContainer,
  DivPaymentTitle,
  DivProductTitle,
  DownloadDocBox,
  FocusRowSumPrice,
  HeaderFixed,
  HeaderItem,
  ImgSlip,
  Price,
  RowDownloadDoc,
  RowSumPrice,
  SelectSpan,
  SpanContent,
  SpanPaymentStatus,
  SpanPaymentType,
  SpanSubHeader,
  SpanSubTitle,
  SpanTitle,
  StyledButton,
  StyledMenuItem,
  StyledSelect,
  WrapperAttachPayment,
  WrapperCalOrder,
  WrapperHeaderFixed,
  WrapperImgSlip,
  WrapperProductCondition,
  WrapperTextField,
  WrapperTextNote,
} from "src/components/order/components";

// Dialog
import PreviewDialog from "src/modules/promo-code/PreviewDialog";
import PaymentDialog from "src/modules/order/purchase-order/modal/PaymentDialog";
import CancelDialog from "src/modules/order/purchase-order/modal/CancelDialog";
import QuotationDialog from "src/modules/order/purchase-order/modal/QuotationDialog";
import CancelRefundDialog from "src/modules/order/purchase-order/modal/CancelRefundDialog";
import EditDocumentDialog from "src/modules/order/purchase-order/modal/EditDocumentDialog";
import EditOrderDialog from "src/modules/order/purchase-order/modal/EditOrderDialog";
import ProductOrderTable from "src/components/order/components/ProductOrderTable";
import { IconMap } from "src/icons";
import AddressLocationDialog from "./modal/AddressLocationDialog";
import EditAddresDialog from "./modal/EditAddresDialog";
// import OrderHistoryDialog from "src/modules/order/purchase-order/modal/OrderHistoryDialog";

function OrderDetail({ open }) {
  const { checkAllowPermission } = useAuth();
  const navigate = useNavigate();
  let { orderId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const { openDialog, closeDialog, closeAllDialogs } = useDialog();
  const [deviceWidth] = useDeviceSize();

  const initialUpdate = { _id: orderId, status: "", internalNote: "" };
  const [updateOrder, setUpdateOrder] = useState(initialUpdate);
  const [orderDetail, setOrderDetail] = useState({});

  const [isLoading, setIsLoading] = useState(false);

  const statusList = [
    {
      label: "รอจ่ายเงิน",
      value: "wp",
      color: "#9A66ED",
      showingStatus: ["in progress", "cancel"],
    },
    {
      label: "รอดำเนินการ",
      value: "in progress",
      color: "#FBB634",
      showingStatus: ["ordered", "cancel & refund"],
    },
    {
      label: "สั่งซื้อสำเร็จ",
      value: "ordered",
      color: "#219653",
      showingStatus: ["cancel & refund"],
    },
    {
      label: "ยกเลิก & คืนเงิน",
      value: "cancel & refund", // ?
      color: "#BF0013",
      showingStatus: [],
    },
    { label: "ยกเลิก", value: "cancel", color: "#BCBCC0", showingStatus: [] },
  ];

  const paymentTypes = [
    { label: "Promptpay", value: "promptpay" },
    { label: "โอนบัญชี XSURFACE", value: "transfer" },
    { label: "บัตรเครดิต / เดบิต", value: "credit-card" },
  ];

  const allowCancel = ["pending", "confirmed", "preparing"];

  useEffect(() => {
    if (orderId === undefined) return;

    fetchOrderDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      setIsLoading(true);
      await getPurchaseOrderDetail(orderId)
        .then(async ({ data: { data } }) => {
          // console.log("----- data -------", data);
          setOrderDetail({
            ...data,
            status: data?.status,
            internalNote: data?.internalNote || "",
          });
          setUpdateOrder({
            ...data,
            status: data?.status,
            internalNote: data?.internalNote || "",
          });
        })
        .catch((err) => {
          console.error("err", err);
          // navigate("/404");
        });
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateInternalNote = async () => {
    try {
      setIsLoading(true);
      const updateParams = { internalNote: updateOrder?.internalNote };

      await updateOrderNote(orderId, updateParams);
      enqueueSnackbar(`Update Note Success!`, { variant: "success" });
      await fetchOrderDetail();
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatus = (e) => {
    let selectedStatus = e.target.value;

    if (selectedStatus === "in progress") {
      openDialog({
        type: "custom",
        customDialog: (
          <PaymentDialog
            orderDetail={orderDetail}
            onClose={() => closeDialog()}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
            setIsLoading={setIsLoading}
          />
        ),
      });
    } else if (selectedStatus === "cancel") {
      openDialog({
        type: "custom",
        customDialog: (
          <CancelDialog
            orderId={orderDetail?._id}
            onClose={() => closeDialog()}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
            setIsLoading={setIsLoading}
          />
        ),
      });
    } else if (selectedStatus === "ordered") {
      openDialog({
        type: "custom",
        customDialog: (
          <QuotationDialog
            orderDetail={orderDetail}
            onClose={() => closeDialog()}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
            setIsLoading={setIsLoading}
          />
        ),
      });
    } else if (selectedStatus === "cancel & refund") {
      openDialog({
        type: "custom",
        customDialog: (
          <CancelRefundDialog
            orderDetail={orderDetail}
            onClose={() => closeDialog()}
            onSuccess={(refundId) => {
              closeAllDialogs();
              fetchOrderDetail();

              setTimeout(() => {
                navigate(`/cancel/detail/${refundId}`);
              }, [2000]);
            }}
            setIsLoading={setIsLoading}
          />
        ),
      });
    }
  };

  const telFormat = (tel) => {
    if (!checkNotEmpty(tel)) return;
    let new_tel = tel.slice(0, 3) + "-" + tel.slice(3, 6) + "-" + tel.slice(6);
    return new_tel;
  };

  const convertPrice = (price) =>
    Intl.NumberFormat("th-TH", { style: "currency", currency: "THB" }).format(
      price,
    );

  useEffect(() => {
    if (isLoading) document.body.style.overflow = "hidden";
    else document.body.style = "none";
  }, [isLoading]);

  const PriceSummary = () => {
    return (
      <WrapperCalOrder>
        <RowSumPrice>
          <Price desc>
            {getLocale() === "th" ? "ราคาสินค้า" : "Product price"}
          </Price>
          <Price>
            {convertIntToCurrency(
              orderDetail?.totalPriceOfProduct ||
                orderDetail?.productChoiceOrders
                  ?.map((product) => product.price * product.volume)
                  .reduce((a, b) => a + b, 0),
            )}{" "}
            บาท
          </Price>
        </RowSumPrice>

        <RowSumPrice>
          <Price desc>
            {getLocale() === "th" ? "ราคาช่าง" : "Service price"}

            {!!orderDetail?.totalInstaller && (
              <span>*ช่างประกอบและติดตั้ง</span>
            )}
          </Price>
          <Price>
            {!!orderDetail?.totalInstaller
              ? convertIntToCurrency(orderDetail?.totalInstaller)
              : convertIntToCurrency(
                  orderDetail?.totalPriceOfService ||
                    orderDetail?.productChoiceOrders
                      ?.filter(
                        (product) =>
                          product?.installDetailGroup?.isService === true,
                      )
                      ?.map(
                        (product) =>
                          (product?.productChoiceOrder?.group_price
                            ?.install_price || 0) *
                            product?.installDetailGroup?.installArea +
                          product?.installDetailGroup?.servicePrice,
                      )
                      .reduce((a, b) => a + b, 0) ||
                    0,
                )}{" "}
            บาท
          </Price>
        </RowSumPrice>

        <RowSumPrice>
          <Price desc>
            {!!orderDetail?.discount?.promoCodeRef?._id
              ? getLocale() === "th"
                ? "คูปองส่วนลด"
                : "Discount coupon"
              : getLocale() === "th"
                ? "ส่วนลด"
                : "Discount"}
            {!!orderDetail?.discount?.promoCodeRef?._id && (
              <span
                style={{ cursor: "pointer" }}
                onClick={() => {
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <PreviewDialog
                        isDetail
                        previewData={{
                          ...orderDetail?.discount?.promoCodeRef,
                          promo_code:
                            orderDetail?.discount?.promoCodeRef?.promoCode,
                          promo_name:
                            orderDetail?.discount?.promoCodeRef?.promoName,
                          promo_title:
                            orderDetail?.discount?.promoCodeRef?.promoTitle,
                          promo_start_time:
                            orderDetail?.discount?.promoCodeRef?.promoStartTime,
                          promo_expire_time:
                            orderDetail?.discount?.promoCodeRef
                              ?.promoExpireTime,
                          promo_pay_type:
                            orderDetail?.discount?.promoCodeRef?.promoPayType,
                          promo_description:
                            orderDetail?.discount?.promoCodeRef
                              ?.promoDescription,
                        }}
                        onClose={() => closeDialog()}
                      />
                    ),
                  });
                }}
              >
                {orderDetail?.discount?.promoCodeRef?.promoCode}
              </span>
            )}
          </Price>
          <Price>
            {convertIntToCurrency(
              orderDetail?.productChoiceOrders
                ?.map((product) => product?.discount)
                .reduce((a, b) => a + b, 0) || 0,
            )}{" "}
            บาท
          </Price>
        </RowSumPrice>

        <RowSumPrice>
          <Price desc>{getLocale() === "th" ? "รวม" : "Subtotal"}</Price>
          <Price>{convertIntToCurrency(orderDetail?.totalPrice)} บาท</Price>
        </RowSumPrice>

        <RowSumPrice>
          <Price desc>{getLocale() === "th" ? "ภาษี 7%" : "Vat 7%"}</Price>
          <Price>{convertIntToCurrency(orderDetail?.vat)} บาท</Price>
        </RowSumPrice>

        {orderDetail?.statusPayment?.paymentType === "credit-card" && (
          <RowSumPrice>
            <Price desc>
              {getLocale() === "th" ? "ค่าธรรมเนียม" : "Credit card fee"}
            </Price>
            <Price>
              {convertIntToCurrency(
                orderDetail?.statusPayment?.depositPrice
                  ? orderDetail?.totalAll * 0.03
                  : 0,
              )}{" "}
              บาท
            </Price>
          </RowSumPrice>
        )}

        {(orderDetail.status === "wp" || orderDetail.status !== "ordered") &&
        orderDetail?.statusPayment?.status !== "deposit" ? (
          <FocusRowSumPrice>
            <Price desc bold>
              {getLocale() === "th" ? "ราคารวมทั้งหมด" : "Grand total"}
            </Price>
            <Price focus>
              <span>
                {orderDetail?.statusPayment?.paymentType === "credit-card"
                  ? convertIntToCurrency(orderDetail?.totalAll * 1.03)
                  : convertIntToCurrency(orderDetail?.totalVat)}
              </span>{" "}
              บาท
            </Price>
          </FocusRowSumPrice>
        ) : (
          <RowSumPrice>
            <Price desc>
              {getLocale() === "th" ? "ราคารวมทั้งหมด" : "Grand total"}
            </Price>
            <Price>
              {orderDetail?.statusPayment?.paymentType === "credit-card"
                ? convertIntToCurrency(orderDetail?.totalAll * 1.03)
                : convertIntToCurrency(orderDetail?.totalVat)}{" "}
              บาท
            </Price>
          </RowSumPrice>
        )}

        {orderDetail?.statusPayment?.status === "deposit" &&
          orderDetail?.outstandingBalance !== 0 && (
            <>
              <RowSumPrice>
                <Price desc>
                  {getLocale() === "th" ? "ยอดมัดจำ" : "Deposit price"}
                </Price>
                <Price>
                  {orderDetail?.statusPayment?.paymentType === "credit-card"
                    ? convertIntToCurrency(
                        orderDetail?.statusPayment?.depositPrice * 1.03,
                      )
                    : convertIntToCurrency(
                        orderDetail?.statusPayment?.status === "deposit"
                          ? orderDetail?.totalAll
                            ? orderDetail?.totalAll
                            : orderDetail?.totalVat
                          : 0,
                      )}{" "}
                  บาท
                </Price>
              </RowSumPrice>

              <FocusRowSumPrice>
                <Price desc bold>
                  {getLocale() === "th" ? "ยอดค้างชำระ" : "Outstanding Balance"}
                </Price>
                <Price focus>
                  <span>
                    {/* {orderDetail?.statusPayment?.paymentType ===
                                  "credit-card"
                                    ? convertIntToCurrency(
                                        orderDetail?.totalAll * 1.03,
                                      )
                                    : convertIntToCurrency(
                                        orderDetail?.outstandingBalance,
                                      )} */}
                    {convertIntToCurrency(orderDetail?.outstandingBalance)}
                  </span>{" "}
                  บาท
                </Price>
              </FocusRowSumPrice>
            </>
          )}
      </WrapperCalOrder>
    );
  };

  return (
    <>
      <Main open={open}>
        <WrapperHeaderFixed>
          <HeaderFixed>
            <HeaderItem>
              <SpanTitle>
                {getLocale() === "th" ? "รายละเอียดใบสั่งซื้อ" : "Order detail"}
              </SpanTitle>
            </HeaderItem>

            <HeaderItem>
              <FormControl sx={{ minWidth: 120, alignSelf: "flex-end" }}>
                <StyledSelect
                  labelId="status-select-label"
                  id="status-select"
                  value={updateOrder?.status}
                  // label={getLocale() === "th" ? "สถานะ" : "Status"}
                  disabled={
                    !checkAllowPermission("orders.can_update_purchaseOrder") ||
                    orderDetail?.status === "cancel" ||
                    (orderDetail?.status === "ordered" &&
                    !allowCancel.includes(
                      orderDetail?.shippingInfo?.status?.value,
                    )
                      ? true
                      : false)
                  }
                  onChange={handleStatus}
                  MenuProps={{
                    PaperProps: {
                      sx: { borderRadius: 4, ".MuiList-root": { padding: 0 } },
                    },
                  }}
                >
                  {statusList.map((option) => {
                    let currentStatus = statusList.find(
                      ({ value }) => value === orderDetail?.status,
                    );

                    return (
                      <StyledMenuItem
                        value={option.value}
                        style={
                          !currentStatus?.showingStatus?.includes(option.value)
                            ? { display: "none" }
                            : {}
                        }
                      >
                        <Stack gap={1} direction="row" alignItems="center">
                          <CircleIcon
                            fontSize={font.size.sm}
                            sx={{ color: option.color }}
                          />
                          <SelectSpan>{option.label}</SelectSpan>
                        </Stack>
                      </StyledMenuItem>
                    );
                  })}
                </StyledSelect>
              </FormControl>
            </HeaderItem>
          </HeaderFixed>
        </WrapperHeaderFixed>

        <Container>
          <Grid container spacing={2}>
            {/* Customer/Order Detail */}
            <Grid item xs={12} sx={{ margin: "0 auto" }}>
              <DivBackground>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12}>
                    <Grid container xs={12}>
                      <Grid item xs={6}>
                        <SpanTitle>
                          {getLocale() === "th"
                            ? "ข้อมูลลูกค้า"
                            : "ข้อมูลลูกค้า"}
                        </SpanTitle>
                      </Grid>

                      {orderDetail?.status !== "ordered" && (
                        <Grid
                          item
                          xs={6}
                          justifyContent="flex-end"
                          display="flex"
                        >
                          <Stack
                            gap={2}
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                          >
                            <StyledButton
                              id="edit-order-button"
                              onClick={() => {
                                openDialog({
                                  type: "custom",
                                  customDialog: (
                                    <EditAddresDialog
                                      orderDetail={orderDetail}
                                      onClose={() => closeDialog()}
                                      onSuccess={() => {
                                        fetchOrderDetail();
                                        closeAllDialogs();
                                      }}
                                      setIsLoading={setIsLoading}
                                    />
                                  ),
                                });
                              }}
                              variant="outlined"
                              startIcon={<EditIcon />}
                            >
                              แก้ไขที่อยู่ลูกค้า
                            </StyledButton>
                          </Stack>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>
                      {getLocale() === "th"
                        ? "หมายเลขคำสั่งซื้อ"
                        : "Order Number"}
                    </SpanSubHeader>
                    <SpanContent>{orderDetail?.orderNumber}</SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>
                      {getLocale() === "th" ? "หมายเลขใบเสนอราคา" : "QT Number"}
                    </SpanSubHeader>
                    <SpanContent>{orderDetail?.qtNumber || "-"}</SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>ชื่อลูกค้า</SpanSubHeader>
                    <SpanContent>
                      {orderDetail?.shippingAddress?.name}
                    </SpanContent>
                  </Grid>

                  {/* <Grid item xs={12} sm={6}>
                    <SpanSubHeader>เลขประจำตัวผู้เสียภาษี</SpanSubHeader>
                    <SpanContent>
                      {orderDetail?.taxInvoiceAddress?.taxId
                        ? orderDetail?.taxInvoiceAddress?.taxId
                        : "-"}
                    </SpanContent>
                  </Grid> */}

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>อีเมล</SpanSubHeader>
                    <SpanContent>{orderDetail?.user?.email}</SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>เบอร์โทรศัพท์</SpanSubHeader>
                    <SpanContent>
                      {telFormat(orderDetail?.shippingAddress?.tel) || "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>ที่อยู่</SpanSubHeader>
                    <SpanContent>
                      {`
                      ${orderDetail?.shippingAddress?.address || ""} ${
                        orderDetail?.shippingAddress?.subdistrict || ""
                      } 
                      ${orderDetail?.shippingAddress?.district || ""} ${
                        orderDetail?.shippingAddress?.province || ""
                      } 
                      ${orderDetail?.shippingAddress?.zipcode || ""}
                      `}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>ออเดอร์อ้างอิง</SpanSubHeader>
                    <SpanContent>
                      {!!orderDetail?.refDraftOrder ? (
                        <Link
                          to={`/sale-order/detail/${orderDetail?.refDraftOrder?._id}`}
                          target="_blank"
                        >
                          {orderDetail?.refDraftOrder?.running_number}
                        </Link>
                      ) : (
                        ` - `
                      )}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <SpanSubHeader>วันที่สั่งซื้อ</SpanSubHeader>
                    <SpanContent>
                      {dayjs(orderDetail?.createdAt).format("DD-MM-YYYY")}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <SpanSubHeader>จัดส่งภายในวันที่</SpanSubHeader>
                    <SpanContent>
                      {!!orderDetail?.scheduleDate
                        ? dayjs(orderDetail?.scheduleDate).format("DD-MM-YYYY")
                        : "-"}
                    </SpanContent>
                  </Grid>

                  {!!orderDetail?.shippingAddress?.recipients?.coordinates
                    ?.lat &&
                    !!orderDetail?.shippingAddress?.recipients?.coordinates
                      ?.lng && (
                      <Grid
                        item
                        xs={12}
                        // display="flex"
                        // alignItems="center"
                        // justifyContent="space-between"
                      >
                        <SpanSubHeader>ตำแหน่งที่ตั้ง</SpanSubHeader>
                        {/* <WrapperMapsContainer>
                          <MapsContainer>
                            <CustomMap
                              markerLocation={{
                                lat: orderDetail?.shippingAddress?.recipients
                                  ?.coordinates?.lat,
                                lng: orderDetail?.shippingAddress?.recipients
                                  ?.coordinates?.lng,
                              }}
                              isViewOnly
                            />
                          </MapsContainer>
                        </WrapperMapsContainer> */}

                        <Button
                          variant="secondary"
                          startIcon={<IconMap fill={Primary} />}
                          sx={{ minWidth: 200 }}
                          onClick={() =>
                            openDialog({
                              type: "custom",
                              customDialog: (
                                <AddressLocationDialog
                                  addressCoordinates={
                                    orderDetail?.shippingAddress?.recipients
                                      ?.coordinates
                                  }
                                  onClose={() => closeDialog()}
                                />
                              ),
                            })
                          }
                        >
                          กดดูตำแหน่งที่ตั้ง
                        </Button>

                        <Button
                          variant="secondary"
                          startIcon={<IconMap color={Primary} />}
                          sx={{ minWidth: 200, margin: "10px" }}
                          onClick={() =>
                            window.open(
                              `https://www.google.com/maps?q=${orderDetail?.shippingAddress?.recipients?.coordinates?.lat},${orderDetail?.shippingAddress?.recipients?.coordinates?.lng}`,
                            )
                          }
                        >
                          ดูตำแหน่งที่ตั้งลูกค้าบน Google Maps
                        </Button>
                      </Grid>
                    )}

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="ใบกำกับภาษี"
                      sx={{
                        margin: 0,
                        ".MuiFormControlLabel-label": {
                          fontSize: "1rem",
                          fontWeight: 400,
                          lineHeight: "24px",
                        },
                        ".MuiCheckbox-root": { p: 0.5, mr: 0.5 },
                      }}
                      control={
                        <Checkbox
                          name="install-price-checkbox"
                          checked={
                            checkNotEmpty(orderDetail?.taxInvoiceAddress?.name)
                              ? true
                              : false
                          }
                          disabled={orderDetail?.status === "ordered"}
                          // onChange={(e) => {
                          //   onChangeUpload(
                          //     null,
                          //     `choices.${choiceIndex}.pre_order_date`,
                          //     "7 - 14  วัน",
                          //   );
                          // }}
                        />
                      }
                    />
                  </Grid>

                  {checkNotEmpty(orderDetail?.taxInvoiceAddress?.name) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12}>
                        <SpanTitle>
                          {getLocale() === "th"
                            ? "ข้อมูลใบกำกับภาษี"
                            : "ข้อมูลใบกำกับภาษี"}
                        </SpanTitle>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <SpanSubHeader>ชื่อ</SpanSubHeader>
                        <SpanContent>
                          {orderDetail?.taxInvoiceAddress?.name}
                        </SpanContent>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <SpanSubHeader>เลขประจำตัวผู้เสียภาษี</SpanSubHeader>
                        <SpanContent>
                          {orderDetail?.taxInvoiceAddress?.taxId
                            ? orderDetail?.taxInvoiceAddress?.taxId
                            : "-"}
                        </SpanContent>
                      </Grid>

                      {orderDetail?.taxInvoiceAddress?.type === "company" &&
                      orderDetail?.taxInvoiceAddress?.companyName !== "" ? (
                        <>
                          <Grid item xs={6}>
                            <SpanSubHeader>ชื่อบริษัท</SpanSubHeader>
                            <SpanContent>
                              {orderDetail?.taxInvoiceAddress?.companyName
                                ? orderDetail?.taxInvoiceAddress?.companyName
                                : "-"}
                            </SpanContent>
                          </Grid>

                          <Grid item xs={6}>
                            <SpanSubHeader>สาขา</SpanSubHeader>
                            <SpanContent>
                              {orderDetail?.taxInvoiceAddress?.branch
                                ? orderDetail?.taxInvoiceAddress?.branch
                                : "-"}
                            </SpanContent>
                          </Grid>
                        </>
                      ) : null}

                      <Grid item xs={12} sm={6}>
                        <SpanSubHeader>เบอร์โทรศัพท์</SpanSubHeader>
                        <SpanContent>
                          {orderDetail?.taxInvoiceAddress?.tel}
                        </SpanContent>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <SpanSubHeader>ที่อยู่จัดส่ง</SpanSubHeader>
                        <SpanContent>
                          {`${orderDetail?.taxInvoiceAddress?.shippingAddress?.address || ""} ${
                            orderDetail?.taxInvoiceAddress?.shippingAddress
                              ?.subdistrict || ""
                          } 
                            ${orderDetail?.taxInvoiceAddress?.shippingAddress?.district || ""} ${
                              orderDetail?.taxInvoiceAddress?.shippingAddress
                                ?.province || ""
                            } 
                            ${orderDetail?.taxInvoiceAddress?.shippingAddress?.zipcode || ""}
                          `}
                        </SpanContent>
                      </Grid>
                    </>
                  )}
                </Grid>
              </DivBackground>
            </Grid>

            {/* Order summary Section */}
            <Grid item xs={12}>
              <DivBackground>
                <Grid container spacing={3}>
                  {/* Order Product List */}
                  <Grid
                    item
                    container
                    xs={12}
                    spacing={2}
                    sx={{ alignItems: "center" }}
                  >
                    <Grid item xs={12}>
                      <DivProductTitle>
                        <SpanTitle>ข้อมูลสินค้า</SpanTitle>

                        {orderDetail?.status === "ordered" && (
                          <SpanSubTitle color={Green}>
                            สำเร็จ วันที่{" "}
                            {dayjs(orderDetail?.shippingInfo?.createdAt).format(
                              "DD-MM-YYYY",
                            )}
                          </SpanSubTitle>
                        )}

                        {orderDetail?.status === "cancel" && (
                          <SpanSubTitle color={Primary}>
                            ยกเลิก วันที่{" "}
                            {dayjs(orderDetail?.updatedAt).format("DD-MM-YYYY")}
                          </SpanSubTitle>
                        )}
                      </DivProductTitle>
                    </Grid>

                    <Grid item sm={5} xs={12}>
                      <SpanSubTitle>
                        ร้านค้า {orderDetail?.sellerName}
                      </SpanSubTitle>
                    </Grid>

                    {/* edit order button */}
                    {checkAllowPermission("orders.can_update_purchaseOrder") &&
                      orderDetail?.status !== "ordered" &&
                      orderDetail?.status !== "cancel" && (
                        <Grid item sm={7} xs={12}>
                          <Stack
                            gap={2}
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                          >
                            <StyledButton
                              id="edit-order-button"
                              onClick={() => {
                                if (
                                  !!orderDetail?.discount?.promoCodeRef?._id
                                ) {
                                  openDialog({
                                    type: "confirm",
                                    title: `การแก้ไขคำสั่งซื้อจะทำให้คูปองส่วนลดไม่สามารถใช้งานได้อีกต่อไป\nต้องการแก้ไขคำสั่งซื้อใช่หรือไม่?`,
                                    onConfirm: () => {
                                      openDialog({
                                        type: "custom",
                                        customDialog: (
                                          <EditOrderDialog
                                            orderDetail={orderDetail}
                                            onClose={() => closeDialog()}
                                            onSuccess={() => {
                                              fetchOrderDetail();
                                              closeAllDialogs();
                                            }}
                                            setIsLoading={setIsLoading}
                                          />
                                        ),
                                      });
                                    },
                                    maxWidth: 400,
                                  });
                                } else {
                                  openDialog({
                                    type: "custom",
                                    customDialog: (
                                      <EditOrderDialog
                                        orderDetail={orderDetail}
                                        onClose={() => closeDialog()}
                                        onSuccess={() => {
                                          fetchOrderDetail();
                                          closeAllDialogs();
                                        }}
                                        setIsLoading={setIsLoading}
                                      />
                                    ),
                                  });
                                }
                              }}
                              variant="outlined"
                              startIcon={<EditIcon />}
                            >
                              {getLocale() === "th"
                                ? "แก้ไขคำสั่งซื้อ"
                                : "Edit order detail"}
                            </StyledButton>
                          </Stack>
                        </Grid>
                      )}

                    {/* // !! Hold: modal edit history */}
                    {/* <Grid item sm={7} xs={12}>
                      <Stack
                        gap={2}
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        {orderDetail?.status !== "ordered" && (
                          <StyledButton
                            id="edit-order-button"
                            onClick={(e) =>
                              openDialog({
                                type: "custom",
                                customDialog: (
                                  <EditOrderDialog
                                    orderDetail={orderDetail}
                                    onClose={() => closeDialog()}
                                    onSuccess={() => {
                                      closeAllDialogs();
                                    }}
                                    setIsLoading={setIsLoading}
                                  />
                                ),
                              })
                            }
                            variant="outlined"
                            startIcon={<EditIcon />}
                          >
                            {getLocale() === "th"
                              ? "แก้ไขคำสั่งซื้อ"
                              : "Edit order detail"}
                          </StyledButton>
                        )}

                        <StyledButton
                          id="order-history-button"
                          onClick={(e) =>
                            openDialog({
                              type: "custom",
                              customDialog: (
                                <OrderHistoryDialog
                                  orderDetail={orderDetail}
                                  onClose={() => closeDialog()}
                                />
                              ),
                            })
                          }
                          variant="outlined"
                          color="secondary"
                          startIcon={<HistoryIcon />}
                        >
                          {getLocale() === "th"
                            ? "ประวัติการแก้ไข"
                            : "Order history"}
                        </StyledButton>
                      </Stack>
                    </Grid> */}

                    <Grid item xs={12}>
                      <ProductOrderTable
                        productOrders={orderDetail?.productChoiceOrders}
                      />
                    </Grid>
                  </Grid>

                  {/* Order summary */}
                  <Grid item container xs={12} spacing={3}>
                    {/* Product condition */}
                    <Grid item sm={6} xs={12}>
                      <SpanTitle>เงื่อนไขสินค้า</SpanTitle>

                      <WrapperProductCondition>
                        <TextField
                          className="TextField-without-border-radius"
                          placeholder="เงื่อนไขสินค้า"
                          fullWidth
                          // disabled={orderDetail?.status === "ordered"}
                          multiline
                          minRows={
                            deviceWidth > 835 ? 12 : deviceWidth <= 540 ? 3 : 10
                          }
                          maxRows={deviceWidth > 540 ? 15 : 7}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ readOnly: true }}
                          value={orderDetail?.condition || ""}
                        />
                      </WrapperProductCondition>
                    </Grid>

                    {/* Order Price Summary */}
                    <Grid item sm={6} xs={12}>
                      <SpanTitle color={Primary}>
                        {getLocale() === "th"
                          ? "สรุปใบสั่งซื้อ"
                          : "Purchase order summary"}
                      </SpanTitle>

                      {PriceSummary()}
                    </Grid>
                  </Grid>

                  {/* Payment Section */}
                  {((orderDetail?.statusPayment?.paymentType !==
                    "credit-card" &&
                    !!orderDetail?.statusPayment?.paySlipImage) ||
                    (orderDetail?.statusPayment?.paymentType ===
                      "credit-card" &&
                      orderDetail?.statusPayment?.status === "paid")) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12}>
                        <DivPaymentContainer
                          single={
                            !!orderDetail?.statusPaymentRePay?.paySlipOtherImage
                              ?._id
                              ? false
                              : true
                          }
                        >
                          {/* payment section */}
                          <Grid container spacing={2}>
                            {/* payment slip */}
                            <Grid item xs={12}>
                              <SpanTitle>การชำระเงิน</SpanTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <DivPaymentTitle>
                                <SpanPaymentType>
                                  {getLocale() === "th"
                                    ? "ชำระเงิน"
                                    : "Payment slip"}
                                </SpanPaymentType>

                                {orderDetail?.statusPayment?.status ===
                                "paid" ? (
                                  <SpanPaymentStatus>
                                    จ่ายเต็มจำนวน
                                  </SpanPaymentStatus>
                                ) : orderDetail?.statusPayment?.status ===
                                  "deposit" ? (
                                  <SpanPaymentStatus>
                                    มัดจำ{" "}
                                    <span>
                                      {`(
                                  ${
                                    orderDetail?.statusPayment?.depositPrice
                                      ? convertPrice(
                                          orderDetail?.statusPayment
                                            ?.depositPrice,
                                          orderDetail?.statusPayment
                                            ?.depositPrice,
                                        )
                                      : 0
                                  }
                                  )`}
                                    </span>
                                    {/* มัดจำ <span>(5,000)</span> */}
                                  </SpanPaymentStatus>
                                ) : null}
                              </DivPaymentTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <DivPaymentTitle>
                                <SpanPaymentType>
                                  ช่องทางการชำระเงิน
                                </SpanPaymentType>

                                <SpanPaymentStatus>
                                  {paymentTypes.find(
                                    (type) =>
                                      orderDetail?.statusPayment
                                        ?.paymentType === type.value,
                                  )?.label || "-"}
                                </SpanPaymentStatus>
                              </DivPaymentTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <DivPaymentTitle>
                                <SpanPaymentType>
                                  วันที่ชำระเงิน
                                </SpanPaymentType>

                                <SpanPaymentStatus>
                                  {!!orderDetail?.statusPayment?.slipDate
                                    ? dayjs(
                                        orderDetail?.statusPayment?.slipDate,
                                      ).format("DD-MM-YYYY")
                                    : "-"}
                                </SpanPaymentStatus>
                              </DivPaymentTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <WrapperTextField>
                                <TextField
                                  className="TextField-without-border-radius"
                                  label="Ref Pay NO."
                                  InputLabelProps={{ shrink: true }}
                                  InputProps={{ readOnly: true }}
                                  sx={{
                                    margin: "auto",
                                    width: "100%",
                                    textAlign: "center",
                                  }}
                                  value={
                                    orderDetail?.statusPayment?.refPayNo || ""
                                  }
                                />
                              </WrapperTextField>
                            </Grid>

                            {!!orderDetail?.statusPayment?.paySlipImage && (
                              <Grid item xs={12}>
                                <RowDownloadDoc>
                                  <WrapperAttachPayment>
                                    <WrapperImgSlip>
                                      <ImgSlip
                                        src={
                                          orderDetail?.statusPayment
                                            ?.paySlipImage?.docUrl
                                        }
                                        alt="paySlipImage"
                                      />
                                    </WrapperImgSlip>

                                    <Button
                                      onClick={() => {
                                        window.open(
                                          `${orderDetail?.statusPayment?.paySlipImage?.docUrl}`,
                                          "_blank",
                                        );
                                      }}
                                      sx={{
                                        padding: "8px 16px",
                                        fontSize: font.size.sm,
                                        "&.MuiButton-outlined": {
                                          border: "1px solid #d9d9d9",
                                        },
                                      }}
                                      variant="outlined"
                                      startIcon={<SaveAltIcon />}
                                    >
                                      {getLocale() === "th"
                                        ? "ดาวน์โหลด"
                                        : "Download"}
                                    </Button>
                                  </WrapperAttachPayment>
                                </RowDownloadDoc>
                              </Grid>
                            )}
                          </Grid>

                          {!!orderDetail?.statusPaymentRePay
                            ?.paySlipOtherImage && (
                            <>
                              {/* desktop */}
                              <Divider
                                orientation="vertical"
                                sx={{ display: { md: "initial", xs: "none" } }}
                              />

                              {/* mobile/tablet */}
                              <Divider sx={{ display: { md: "none" } }} />

                              {/* payment section */}
                              <Grid container spacing={2}>
                                {/* payment other slip */}
                                <Grid item xs={12}>
                                  <SpanTitle>การชำระเงิน</SpanTitle>
                                </Grid>

                                <Grid item xs={12}>
                                  <DivPaymentTitle>
                                    <SpanPaymentType>
                                      {getLocale() === "th"
                                        ? "ชำระเงิน"
                                        : "Payment slip"}
                                    </SpanPaymentType>

                                    {orderDetail?.statusPaymentRePay?.status ===
                                      "paid" && (
                                      <SpanPaymentStatus>
                                        จ่ายเต็มจำนวน
                                      </SpanPaymentStatus>
                                    )}
                                  </DivPaymentTitle>
                                </Grid>

                                <Grid item xs={12}>
                                  <DivPaymentTitle>
                                    <SpanPaymentType>
                                      ช่องทางการชำระเงิน
                                    </SpanPaymentType>

                                    <SpanPaymentStatus>
                                      {paymentTypes.find(
                                        (type) =>
                                          orderDetail?.statusPaymentRePay
                                            ?.paymentType === type.value,
                                      )?.label || "-"}
                                    </SpanPaymentStatus>
                                  </DivPaymentTitle>
                                </Grid>

                                <Grid item xs={12}>
                                  <DivPaymentTitle>
                                    <SpanPaymentType>
                                      วันที่ชำระเงิน
                                    </SpanPaymentType>

                                    <SpanPaymentStatus>
                                      {!!orderDetail?.statusPaymentRePay
                                        ?.slipOtherDate
                                        ? dayjs(
                                            orderDetail?.statusPaymentRePay
                                              ?.slipOtherDate,
                                          ).format("DD-MM-YYYY")
                                        : "-"}
                                    </SpanPaymentStatus>
                                  </DivPaymentTitle>
                                </Grid>

                                <Grid item xs={12}>
                                  <WrapperTextField>
                                    <TextField
                                      className="TextField-without-border-radius"
                                      label="Ref Pay NO."
                                      InputLabelProps={{ shrink: true }}
                                      InputProps={{ readOnly: true }}
                                      sx={{
                                        margin: "auto",
                                        width: "100%",
                                        textAlign: "center",
                                      }}
                                      value={
                                        orderDetail?.statusPaymentRePay
                                          ?.refPayNo || ""
                                      }
                                    />
                                  </WrapperTextField>
                                </Grid>

                                <Grid item xs={12}>
                                  <RowDownloadDoc>
                                    <WrapperAttachPayment>
                                      <WrapperImgSlip>
                                        <ImgSlip
                                          src={
                                            orderDetail?.statusPaymentRePay
                                              ?.paySlipOtherImage?.docUrl
                                          }
                                          alt="paySlipOtherImage"
                                        />
                                      </WrapperImgSlip>

                                      <Button
                                        onClick={() => {
                                          window.open(
                                            `${orderDetail?.statusPaymentRePay?.paySlipOtherImage?.docUrl}`,
                                            "_blank",
                                          );
                                        }}
                                        sx={{
                                          padding: "8px 16px",
                                          fontSize: font.size.sm,
                                          "&.MuiButton-outlined": {
                                            border: "1px solid #d9d9d9",
                                          },
                                        }}
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                      >
                                        {getLocale() === "th"
                                          ? "ดาวน์โหลด"
                                          : "Download"}
                                      </Button>
                                    </WrapperAttachPayment>
                                  </RowDownloadDoc>
                                </Grid>
                              </Grid>
                            </>
                          )}
                        </DivPaymentContainer>
                      </Grid>
                    </>
                  )}

                  {orderDetail?.status === "ordered" && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      {/* Deposit receipt */}
                      <Grid item container xs={12} spacing={3}>
                        <Grid item container sm={6} xs={12} spacing={1}>
                          <Grid item xs={12}>
                            <SpanTitle>ใบเสร็จรับเงินชั่วคราว</SpanTitle>
                          </Grid>

                          <Grid item xs={12}>
                            <DownloadDocBox single>
                              <StyledButton
                                fullWidth
                                onClick={() => {
                                  window.open(
                                    `${orderDetail?.receiptPdf?.docUrl}`,
                                    "_blank",
                                  );
                                }}
                                variant="outlined"
                                color="secondary"
                                startIcon={<AttachFileIcon />}
                              >
                                ใบเสร็จรับเงินชั่วคราว
                              </StyledButton>
                            </DownloadDocBox>
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
              </DivBackground>
            </Grid>

            {/* Download receipt */}
            {!!orderDetail?.quotationPdf &&
              orderDetail?.status === "ordered" && (
                <Grid item xs={12}>
                  <DivBackground>
                    <SpanTitle mb={8}>เอกสารสำหรับลูกค้า</SpanTitle>

                    {/* Customer document */}
                    <Grid container spacing={3}>
                      <Grid item container sm={6} xs={12} spacing={1}>
                        <Grid item xs={12}>
                          <Stack direction="row" alignItems="center" gap={2}>
                            <SpanSubTitle>ใบเสนอราคา</SpanSubTitle>

                            {checkAllowPermission(
                              "orders.can_update_purchaseOrder",
                            ) && (
                              <Button
                                sx={{ px: 2, py: 1 }}
                                onClick={() =>
                                  openDialog({
                                    type: "custom",
                                    customDialog: (
                                      <EditDocumentDialog
                                        orderId={orderDetail?._id}
                                        quoData={{
                                          qtNumber: orderDetail?.qtNumber || "",
                                          quotationPdf:
                                            orderDetail?.quotationPdf || "",
                                          quotationOtherPdf:
                                            orderDetail?.quotationOtherPdf ||
                                            "",
                                        }}
                                        onClose={() => closeDialog()}
                                        onSuccess={() => {
                                          closeAllDialogs();
                                          fetchOrderDetail();
                                        }}
                                      />
                                    ),
                                  })
                                }
                                startIcon={
                                  <EditIcon
                                    sx={{
                                      color: Primary,
                                      fontSize: font.size.md,
                                    }}
                                  />
                                }
                              >
                                แก้ไข
                              </Button>
                            )}
                          </Stack>
                        </Grid>

                        <Grid item xs={12}>
                          <DownloadDocBox
                            single={
                              !!orderDetail?.quotationOtherPdf ? false : true
                            }
                          >
                            <StyledButton
                              fullWidth
                              onClick={() => {
                                window.open(
                                  `${orderDetail?.quotationPdf?.docUrl}`,
                                  "_blank",
                                );
                              }}
                              variant="outlined"
                              color="secondary"
                              startIcon={<AttachFileIcon />}
                            >
                              ใบเสนอราคา
                            </StyledButton>

                            {!!orderDetail?.quotationOtherPdf && (
                              <StyledButton
                                fullWidth
                                onClick={() => {
                                  window.open(
                                    `${orderDetail?.quotationOtherPdf?.docUrl}`,
                                    "_blank",
                                  );
                                }}
                                variant="outlined"
                                color="secondary"
                                startIcon={<AttachFileIcon />}
                              >
                                เพิ่มเติม
                              </StyledButton>
                            )}
                          </DownloadDocBox>
                        </Grid>
                      </Grid>
                    </Grid>
                  </DivBackground>
                </Grid>
              )}

            {/* Note */}
            <Grid item xs={12}>
              <DivBackground>
                <SpanTitle>{getLocale() === "th" ? "โน๊ต" : "Note"}</SpanTitle>

                <WrapperTextNote>
                  <TextField
                    id="internal-note-input"
                    placeholder={getLocale() === "th" ? "โน๊ต" : "Note"}
                    fullWidth
                    multiline
                    rows={6}
                    onChange={(e) => {
                      setUpdateOrder({
                        ...updateOrder,
                        internalNote: e.target.value,
                      });
                    }}
                    value={updateOrder?.internalNote}
                  />
                </WrapperTextNote>

                {checkAllowPermission("orders.can_update_purchaseOrder") &&
                updateOrder?.internalNote !== orderDetail?.internalNote ? (
                  <Stack alignItems="flex-end" sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        updateInternalNote();
                      }}
                      sx={{ minWidth: 120 }}
                    >
                      {getLocale() === "th" ? "บันทึก" : "Save"}
                    </Button>
                  </Stack>
                ) : null}
              </DivBackground>
            </Grid>
          </Grid>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
}

export default OrderDetail;
